import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Pagination,
} from "@mui/material";
import { Edit, Delete } from "@mui/icons-material";
import axios from "axios";
import BASE_URL from "../config";

const FeeCategoryPage = () => {
  const [categories, setCategories] = useState([]);
  const [courses, setCourses] = useState([]);
  const [formData, setFormData] = useState({
    CategoryName: "",
    CourseID: "",
    Type: "1",
    CreatedBy: sessionStorage.getItem("adminName"),
    CreatedDate: new Date().toISOString().split("T")[0],
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", type: "" });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchCategories();
    fetchCourses();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/feecategories`);
      //const Date = new Date(response.CreatedDate);
      const formattedData = response.data.map((category) => ({
        ...category,
        CreatedDate: new Date(category.CreatedDate).toLocaleDateString("en-GB"), // Format date as dd-MM-yyyy
      }));
      setCategories(formattedData);
    } catch (error) {
      console.error("Failed to fetch categories", error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/api/course`);
      setCourses(response.data);
    } catch (error) {
      console.error("Failed to fetch courses", error);
    }
  };

  const validateInputs = () => {
    if (!formData.CategoryName.trim()) return "Category Name is required.";
    if (!formData.CourseID) return "Course selection is required.";
    return null;
  };

  const handleSubmit = async () => {
    const validationError = validateInputs();
    if (validationError) {
      setSnackbar({ open: true, message: validationError, type: "error" });
      return;
    }

    try {
      if (editingCategory) {
        const updatedFormData = {
          ...formData,
          UpdatedBy: sessionStorage.getItem("adminName"),
          UpdatedDate: new Date().toISOString().split("T")[0],
        };
        await axios.put(`${BASE_URL}/api/feecategories/${editingCategory.CategoryID}`, updatedFormData);
        setSnackbar({ open: true, message: "Category updated successfully!", type: "success" });
      } else {
        await axios.post(`${BASE_URL}/api/feecategories`, formData);
        setSnackbar({ open: true, message: "Category added successfully!", type: "success" });
      }
      fetchCategories();
      setFormData({
        CategoryName: "",
        CourseID: "",
        Type: "1",
        CreatedBy: sessionStorage.getItem("adminName"),
        CreatedDate: new Date().toISOString().split("T")[0],
      });
      setEditingCategory(null);
    } catch (error) {
        if (error.response && error.response.status === 400) {
            setSnackbar({ open: true, message: error.response.data.message, type: "error" });
          } else {
            setSnackbar({ open: true, message: "Operation failed!", type: "error" });
          }
          console.error("Failed to submit category", error);
      /* setSnackbar({ open: true, message: "Operation failed!", type: "error" });
      console.error("Failed to submit category", error); */
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      CategoryName: category.CategoryName,
      CourseID: category.CourseID,
      Type: category.Type.toString(),
      CreatedBy: category.CreatedBy,
      CreatedDate: category.CreatedDate,
     
    });
    const container = document.querySelector('#root');
    container.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${BASE_URL}/api/feecategories/${id}`);
      setSnackbar({ open: true, message: "Category deleted successfully!", type: "success" });
      fetchCategories();
    } catch (error) {
      setSnackbar({ open: true, message: "Failed to delete category.", type: "error" });
      console.error("Failed to delete category", error);
    }
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  // Filter and paginate data
  const filteredCategories = categories.filter((category) =>
    category.CategoryName.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const paginatedCategories = filteredCategories.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <Box p={4}>
      <Typography variant="h4" gutterBottom align="center">
        Fee Category Management
      </Typography>
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        gap={2}
        mb={2}
        justifyContent="center"
      >
        <TextField
          label="Category Name"
          value={formData.CategoryName}
          onChange={(e) => setFormData({ ...formData, CategoryName: e.target.value })}
          fullWidth
        />
        <TextField
          select
          label="Course"
          value={formData.CourseID}
          onChange={(e) => setFormData({ ...formData, CourseID: e.target.value })}
          fullWidth
        >
          {courses.map((course) => (
            <MenuItem key={course.CourseID} value={course.CourseID}>
              {course.CourseName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="Type"
          value={formData.Type}
          onChange={(e) => setFormData({ ...formData, Type: e.target.value })}
          fullWidth
        >
          <MenuItem value="1">Regular</MenuItem>
          <MenuItem value="0">Non-Regular</MenuItem>
        </TextField>
        <Button variant="contained" color="primary" onClick={handleSubmit}>
          {editingCategory ? "Update Category" : "Add Category"}
        </Button>
      </Box>

      <Box mb={2}>
        <TextField
          label="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          fullWidth
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
        <TableHead>
  <TableRow>
    <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
      Category Name
    </TableCell>
    <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
      Course
    </TableCell>
    <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
      Type
    </TableCell>
    <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
      Created By
    </TableCell>
    <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
      Created Date
    </TableCell>
    <TableCell align="center" sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
      Actions
    </TableCell>
  </TableRow>
</TableHead>
          <TableBody>
            {paginatedCategories.map((category) => (
              <TableRow key={category.CategoryID}>
                <TableCell  align="center">{category.CategoryName}</TableCell>
                <TableCell  align="center">{category.CourseName}</TableCell>
                <TableCell  align="center">{category.Type === 1 ? "Regular" : "Non-Regular"}</TableCell>
                <TableCell  align="center">{category.CreatedBy}</TableCell>
                <TableCell  align="center">{category.CreatedDate}</TableCell>
                <TableCell>
                  <IconButton color="primary" onClick={() => handleEdit(category)}>
                    <Edit />
                  </IconButton>
                  <IconButton color="secondary" onClick={() => handleDelete(category.CategoryID)}>
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box display="flex" justifyContent="center" mt={2}>
        <Pagination
          count={Math.ceil(filteredCategories.length / rowsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: "", type: "" })}
      >
        <Alert
          onClose={() => setSnackbar({ open: false, message: "", type: "" })}
          severity={snackbar.type}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeeCategoryPage;
